import { Menu, MenuProps, ConfigProvider } from "antd";
import Sider from "antd/es/layout/Sider";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import "../../../../../assets/fontello/css/fontello.css";
import { ROUTE_PATHS } from "../../../../../constants/router.constants";
import { collapsedState } from "../../../../../states/sidebar";
import "./index.scss";

function SiderMenu() {
  const location = useLocation();
  const { t } = useTranslation();
  const isCollapsed = useRecoilValue(collapsedState);
  const setCollapsedState = useSetRecoilState(collapsedState);
  const appVersion = process.env.REACT_APP_APP_VERSION;

  type MenuItem = Required<MenuProps>["items"][number];

  const getSelectedKeyFromPath = (pathname: string, menuItems: MenuProps["items"]): string | undefined => {
    let matchedKey: string | undefined;
    let maxMatchLength = 0;
  
    const findMatchingKey = (items: MenuProps["items"] | undefined) => {
      for (const item of items || []) {
        if (item && typeof item.key === "string" && pathname.startsWith(item.key)) {
          if (item.key.length > maxMatchLength) {
            matchedKey = item.key;
            maxMatchLength = item.key.length;
          }
        }
        
        if (item && 'children' in item) {
          findMatchingKey(item.children);
        }
      }
    };
  
    findMatchingKey(menuItems);
    return matchedKey;
  };
    
  
  function getItem(
    label: React.ReactNode,
    key?: React.Key | null,
    icon?: React.ReactNode,
    children?: MenuItem[]
  ): MenuItem {
    return { key, icon, children, label } as MenuItem;
  }

  let iconAfter = <i className="icon-after icon-arrow---right-2"></i>;
  let iconAfterParent = <i className="icon-after icon-down-open-big"></i>;

  const items: MenuItem[] = [
    getItem(
      <Link to={ROUTE_PATHS.Home}>
        {t("menuDashboard.dashboard")} {iconAfter}
      </Link>,
      ROUTE_PATHS.Home,
      <i className="menu-icon icon-home" />
    ),
    getItem(
      <Link to={ROUTE_PATHS.Alerts}>
        {t("menuDashboard.alerts")} {iconAfter}
      </Link>,
      ROUTE_PATHS.Alerts,
      <i className="menu-icon icon-notification" />
    ),
    getItem(
      <> {t("menuDashboard.users")} {iconAfterParent}</>,
      null,
      <i className="menu-icon icon-2-user" />,
      [
        getItem(
          <Link to={ROUTE_PATHS.Users}>
            {t("menuDashboard.users")} {iconAfter}
          </Link>,
          ROUTE_PATHS.Users,
          null
        ),
        getItem(
          <Link to={ROUTE_PATHS.UserRoles}>
            {t("menuDashboard.userRoles")} {iconAfter}
          </Link>,
          ROUTE_PATHS.UserRoles,
          null
        ),
      ]
    ),
    getItem(
      <Link to={ROUTE_PATHS.TerminalManagement}>Device Management {iconAfter}
      </Link>,
      ROUTE_PATHS.TerminalManagement,
      <i className="menu-icon icon-mobile" />
    ),
    getItem(
      <Link to={ROUTE_PATHS.ApiManagement}>
        {t("menuDashboard.apiManagement")} {iconAfter}
      </Link>,
      ROUTE_PATHS.ApiManagement,
      <i className="menu-icon icon-key" />
    ),
    getItem(
      <Link to={ROUTE_PATHS.Audits}>
        {t("menuDashboard.audits")} {iconAfter}
      </Link>,
      ROUTE_PATHS.Audits,
      <i className="menu-icon icon-folder" />
    ),
    getItem(
      <Link to={ROUTE_PATHS.Reports}>
        {t("menuDashboard.reports")} {iconAfter}
      </Link>,
      ROUTE_PATHS.Reports,
      <i className="menu-icon icon-document" />
    ),
    getItem(
      <Link to={ROUTE_PATHS.SystemConfig}>
        {t("menuDashboard.systemConfig")} {iconAfter}
      </Link>,
      ROUTE_PATHS.SystemConfig,
      <i className="menu-icon icon-category" />
    ),
  ];

  const selectedKey = getSelectedKeyFromPath(location.pathname, items) || location.pathname;


  return (
    <Sider
      className="sider-menu"
      width={296}
      breakpoint="xl"
      collapsedWidth="0"
      trigger={null}
      collapsed={isCollapsed.isCollapsed}
      onCollapse={(value) => setCollapsedState({ isCollapsed: value })}>
      <Menu
        theme={"light"}
        className="body-regular"
        defaultSelectedKeys={[selectedKey]}
        mode="inline"
        defaultOpenKeys={["Dashboard"]}
        items={items}>
      </Menu>
      <p className="app-version">{appVersion}</p>
    </Sider>
  );
}

export default SiderMenu;
